import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, ButtonGroup } from 'reactstrap';
import { formValueSelector } from 'redux-form';
import Loader from '../../../common/Loader';
import SeatReleasePermission from './SeatReleasePermission';
import { SEAT_RELEASE_STATUS_FILTERS } from '../../../../config/constants';
import {
  clearSeatReleasePermissions,
  postExecuteSeatReleaseByIds,
} from '../../../../actions';
import withEndpointAuthorization from '../../../units/authorization/withEndPointAuthorization';
import { VIEW_SEAT_RELEASE_ENDPOINT } from '../../../../config/endpoints';
import { PUT } from '../../../../config/permissions';
import SeatReleasePermissionsButton from './SeatReleasePermissionsButton';

const SeatReleasePermissions = ({
  seatReleasePermissions,
  loading,
  dispatchClearSeatReleasePermissions,
  dispatchPostExecuteSeatReleaseByIds,
  sourceValue,
  destinationValue,
  timeValue,
  numPassengersValue,
  dispatchGetItineraries,
}) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(
    () => () => {
      dispatchClearSeatReleasePermissions();
    },
    [],
  );

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setSelectedIds([]);
  };

  const handleFloorChange = (floor) => {
    setFloorFilter(floor);
    setSelectedIds([]);
  };

  const handleToggle = (id) => {
    const already = selectedIds.indexOf(id) !== -1;
    setSelectedIds(
      already
        ? selectedIds.filter((sid) => sid !== id)
        : selectedIds.concat([id]),
    );
  };

  const handleRelease = async () => {
    await dispatchPostExecuteSeatReleaseByIds(selectedIds);
    const searchParams = {
      source: sourceValue,
      destination: destinationValue,
      time: timeValue,
      numPassengers: numPassengersValue,
    };
    dispatchGetItineraries(searchParams);
    setSelectedIds([]);
    setStatusFilter('all');
    setFloorFilter('all');
  };

  if (loading) {
    return (
      <div className="card mt-3">
        <div className="card-body p-3">
          <h5 className="card-title">Liberación de asientos</h5>
          <Loader />
        </div>
      </div>
    );
  }

  const permissions = seatReleasePermissions || [];

  if (permissions.length === 0) {
    return (
      <div className="card mt-3">
        <div className="card-body p-3">
          <h5 className="card-title">Liberación de asientos</h5>
          <i className="text-muted">
            Aquí se mostrarán los asientos que se podrán liberar
          </i>
          <p className="text-center"> - </p>
        </div>
      </div>
    );
  }

  const floors = [...new Set(permissions.map((p) => p.floor))].sort();
  const singleFloor = floors.length === 1;

  const visible = permissions
    .filter((p) => statusFilter === 'all' || p.status === statusFilter)
    .filter((p) => floorFilter === 'all' || p.floor === floorFilter);

  const counts = {
    all: permissions.length,
    ACTIVE: permissions.filter((p) => p.status === 'ACTIVE').length,
    USED: permissions.filter((p) => p.status === 'USED').length,
  };

  return (
    <div className="card mt-3">
      <div className="card-body p-3">
        <h5 className="card-title">Liberación de asientos</h5>

        <div className="mb-2">
          <div className="mb-2">
            <small className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
              Estado
            </small>
            <ButtonGroup className="w-100" size="sm">
              {SEAT_RELEASE_STATUS_FILTERS.map((f) => (
                <Button
                  key={f.key}
                  color={
                    statusFilter === f.key ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => handleStatusChange(f.key)}
                >
                  {f.label} ({counts[f.key]})
                </Button>
              ))}
            </ButtonGroup>
          </div>

          <div>
            <small className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
              Piso
            </small>
            <ButtonGroup className="w-100" size="sm">
              {singleFloor ? (
                <Button color="primary" disabled>
                  Piso {floors[0]}
                </Button>
              ) : (
                <>
                  <Button
                    color={
                      floorFilter === 'all' ? 'primary' : 'outline-secondary'
                    }
                    onClick={() => handleFloorChange('all')}
                  >
                    Todos
                  </Button>
                  {floors.map((f) => (
                    <Button
                      key={f}
                      color={
                        floorFilter === f ? 'primary' : 'outline-secondary'
                      }
                      onClick={() => handleFloorChange(f)}
                    >
                      Piso {f}
                    </Button>
                  ))}
                </>
              )}
            </ButtonGroup>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
            gap: 8,
          }}
        >
          {visible.map((p) => (
            <SeatReleasePermission
              key={p.id}
              id={p.id}
              status={p.status}
              seat={p.seat}
              floor={p.floor}
              selected={selectedIds.indexOf(p.id) !== -1}
              onToggle={handleToggle}
            />
          ))}
        </div>

        <div className="border-top mt-3 pt-3">
          {selectedIds.length > 0 && (
            <small className="text-muted d-block mb-2">
              {selectedIds.length} asiento{selectedIds.length !== 1 ? 's' : ''}{' '}
              seleccionado{selectedIds.length !== 1 ? 's' : ''}
            </small>
          )}
          <SeatReleasePermissionsButton
            selectedIds={selectedIds}
            handleRelease={handleRelease}
          />
        </div>
      </div>
    </div>
  );
};

SeatReleasePermissions.propTypes = {
  seatReleasePermissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      seat: PropTypes.number.isRequired,
      floor: PropTypes.number.isRequired,
    }),
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  dispatchClearSeatReleasePermissions: PropTypes.func.isRequired,
  sourceValue: PropTypes.number,
  destinationValue: PropTypes.number,
  timeValue: PropTypes.string,
  numPassengersValue: PropTypes.number,
  dispatchPostExecuteSeatReleaseByIds: PropTypes.func.isRequired,
  dispatchGetItineraries: PropTypes.func.isRequired,
};

SeatReleasePermissions.defaultProps = {
  sourceValue: null,
  destinationValue: null,
  timeValue: null,
  numPassengersValue: null,
};

const mapStateToProps = (state, ownProps) => {
  const selector = formValueSelector(ownProps.formName);

  return {
    seatReleasePermissions: state.BookingUnit.Booking.getIn([
      'seatReleasePermission',
      'content',
      'content',
    ]),
    loading: state.BookingUnit.Booking.getIn([
      'seatReleasePermission',
      'loading',
    ]),
    sourceValue: selector(state, 'source'),
    destinationValue: selector(state, 'destination'),
    timeValue: selector(state, 'time'),
    numPassengersValue: selector(state, 'numPassengers'),
  };
};

const mapDispatchToProps = {
  dispatchClearSeatReleasePermissions: clearSeatReleasePermissions,
  dispatchPostExecuteSeatReleaseByIds: postExecuteSeatReleaseByIds,
};

const ConnectedSeatReleasePermissions = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SeatReleasePermissions);

export default withEndpointAuthorization({
  url: VIEW_SEAT_RELEASE_ENDPOINT,
  permissionType: PUT,
})(ConnectedSeatReleasePermissions);
